import { type CollectionModel, type CollectionModelFieldVector, SpanSubFormAdaptor } from '@ikegps/core-shared';

export interface DuplicateLinkCheck {
	hasDuplicate: boolean;
	existingSpanTitle?: string;
	existingSpanInstanceId?: string;
}

/**
 * Check if a collection already has a span linking to the target collection.
 * Uses generic span utilities - works for ANY form with SubFormType.SPAN.
 *
 * @param sourceCollection - The collection initiating the link
 * @param targetCollectionId - The collection ID being linked to
 * @param currentField - The field initiating the link (excluded from check)
 * @returns DuplicateLinkCheck indicating if duplicate exists
 */
export function checkForDuplicateSpanLink(
	sourceCollection: CollectionModel,
	targetCollectionId: string,
	currentField: CollectionModelFieldVector
): DuplicateLinkCheck {
	// Check if collection has a span subform (works for ANY form)
	const { spanSubForm } = sourceCollection;
	if (!spanSubForm) {
		return { hasDuplicate: false };
	}

	// Get all span adaptors using generic utility
	const spans = spanSubForm.value.map((instance) => new SpanSubFormAdaptor(instance));

	// Check each span
	for (const span of spans) {
		// Skip the current field (allow re-linking same span)
		if (span.spanVectorField.id === currentField.id) {
			continue;
		}

		// Check if this span already links to the target
		if (span.spanTargetCollectionId === targetCollectionId) {
			return {
				hasDuplicate: true,
				existingSpanTitle: span.instance.title || span.instance.formTitle,
				existingSpanInstanceId: span.instance.instanceId,
			};
		}
	}

	return { hasDuplicate: false };
}

/**
 * Validate span link is allowed before proceeding.
 * Works for ANY form with SubFormType.SPAN.
 *
 * @param sourceCollection - The collection initiating the link
 * @param targetCollectionId - The collection ID being linked to
 * @param currentField - The field initiating the link (excluded from check)
 * @returns Validation result with error details if invalid
 */
export function validateSpanLink(
	sourceCollection: CollectionModel,
	targetCollectionId: string,
	currentField: CollectionModelFieldVector
): { valid: boolean; error?: string; existingSpan?: string } {
	const duplicateCheck = checkForDuplicateSpanLink(sourceCollection, targetCollectionId, currentField);

	if (duplicateCheck.hasDuplicate) {
		return {
			valid: false,
			error: 'DUPLICATE_LINK',
			existingSpan: duplicateCheck.existingSpanTitle,
		};
	}

	return { valid: true };
}
